<script setup>
import { computed, reactive, ref } from "vue";
import { confirmSignUp } from 'aws-amplify/auth';
import { storeCurrentSession } from "@/utils/cognito";
import { useRoute, useRouter } from "vue-router";
import useAlert from "../../hooks/alert";
import WawLabel from "../ui/Label.vue";
import WawButton from "../ui/Button.vue";
import { useVuelidate } from '@vuelidate/core';
import { required, email, minLength, maxLength, helpers } from '@vuelidate/validators';
import {APP} from "@/config/app";
import Button from "@Atoms/Button.vue";
import AtomButton from "@Atoms/Button.vue";

const route = useRoute();
const router = useRouter();

let loading = ref(false);

// reactive reference to data input fields
const confirmAccountForm = reactive({
  code: "",
  username: route.query.username
})

// set the validation rules
const confirmAccountFormRules = computed(() => {
  return {
    username: {
      required: helpers.withMessage('Please enter your username', required),
      email: helpers.withMessage('Please enter a valid email address', email),
    },
    code: {
      required: helpers.withMessage('Please enter the 6-digit code that was emailed to you', required),
      minLength: helpers.withMessage('The code must be 6 digits', minLength(6)),
      maxLength: helpers.withMessage('The code must be 6 digits', maxLength(6)),
    }
  }
})
const v$ = useVuelidate(confirmAccountFormRules, confirmAccountForm);

// sets up hook for message alerting
const { message, messageStyleType, setMessage } = useAlert();

// confirms uses code when they sign up for an account
async function confirmCode() {
  // runs form validation code
  const validationResult = await v$.value.$validate();
  if(!validationResult){
    return;
  }

  loading.value = true;

  await handleSignUpConfirmation({
    username: confirmAccountForm.username,
    confirmationCode: confirmAccountForm.code
  })
}

async function handleSignUpConfirmation( { username, confirmationCode } ) {
  try {
    const { isSignUpComplete, nextStep } = await confirmSignUp({ username, confirmationCode });

    if(isSignUpComplete){
      await storeCurrentSession(true);

      await router.push({
        name: "Login",
        query: {
          username: confirmAccountForm.username,
          accountConfirmed: true, // You have successfully confirmed your account. Please log in to continue.
          marketing: route.query.marketing,
        },
      });
    }

  } catch ({ message }) {
    setMessage(message, 'danger');
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <section class="bg-sandstone dark:bg-teal py-20 lg:py-28 px-10 lg:px-0">
    <div class="flex flex-col gap-12 sm:mx-auto sm:w-full sm:max-w-lg auth-padding">
      <div class="flex flex-col text-center">
        <h1 class="text-granite dark:text-white">Verify your email address</h1>
        <div :class="APP.MyWealthMode ? 'std-text mt-6 mb-0 pb-0' : 'text-base mt-6 text-mwi-rebrand-black1 dark:text-white font-madera'">
            <span>
              Please enter the 6-digit code that has been emailed to you
            </span>
        </div>
      </div>

      <base-message :type="messageStyleType" v-if="message">{{ message }}</base-message>

      <div class="sm:mx-auto sm:w-full sm:max-w-lg">
        <form @submit.prevent="confirmCode" class="space-y-6">
          <div>
            <WawLabel for="username">Username</WawLabel>
            <div class="mt-2">
              <input
                  type="text"
                  id="username"
                  v-model.trim="confirmAccountForm.username"
                  autocomplete
                  class="text-input"
                  :class="APP.MyWealthMode ? '' : 'font-madera dark:text-granite'"/>

            </div>
            <div v-if="v$.username.$errors.length" class="my-3 rounded-l bg-red-50 p-3">
                <span v-for="error in v$.username.$errors" :key="error.$uid" class="text-red-400">
                  {{ error.$message }}
                </span>
            </div>
          </div>

          <div>
            <WawLabel for="code">Code</WawLabel>
            <div class="mt-2">
              <input
                  type="text"
                  id="code"
                  v-model.trim="confirmAccountForm.code"
                  autocomplete="false"
                  class="text-input"
                  :class="APP.MyWealthMode ? '' : 'font-madera dark:text-granite'"/>
            </div>
            <div v-if="v$.code.$errors.length" class="my-3 rounded-l bg-red-50 p-3">
                <span v-for="error in v$.code.$errors" :key="error.$uid" class="text-red-400">
                  {{ error.$message }}
                </span>
            </div>
          </div>

          <div class="flex flex-row justify-center items-center">
            <Button
              type="submit"
              button-type="unset"
              :class="[APP.MyWealthMode ? 'primary-mwp' : 'primary-mwi', 'h-full button-padding body-3-semi-bold rounded-full max-w-max']"
              :isLoading="loading"
              aria-label="Confirm Account"
            >Confirm Account</Button>
          </div>
        </form>

      </div>

    </div>
  </section>
</template>

<style></style>
